import React, { useCallback } from 'react';

import { NumberInput } from '@rbilabs/components-library/build/components/number-input';
import { useIntl } from 'react-intl';

import { useRedeemReward } from 'components/cart-item/redeem-reward/use-redeem-reward';
import ItemUnavailableMessage from 'components/item-unavailable-message';
import { useGetUrlSearchParam } from 'hooks/location/use-get-url-search-param';
import { useProductPrice } from 'hooks/menu/use-product-price';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyRewards } from 'state/loyalty/hooks/use-loyalty-rewards';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ClickEventComponentNames } from 'state/mParticle/constants';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { useProductWizardContext } from 'state/product-wizard';
import { useEditingCartEntry } from 'state/product-wizard/hooks/use-editing-cart-entry';
import { MIN_CART_QUANTITY, getMenuObjectCartQuantity } from 'utils/cart';

import { StyledActionButton, StyledNumberInputWrapper } from '../styled';
import { useProductAvailability } from '../use-product-availability';
import { useProductCart } from '../use-product-cart';

import { ProductCtaWrapper } from './product-cta-wrapper';

export const ProductHomeCta: React.FC = () => {
  const { isOffer } = useMenuContext();
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { loyaltyUser } = useLoyaltyUser();
  const { cartEntries } = useOrderContext();
  const { productQuantity, selectedProduct, setProductQuantity } = useProductWizardContext();

  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { addToOrder } = useProductCart();
  const editingCartEntry = useEditingCartEntry();
  const cartIdEditing = editingCartEntry?.cartId;

  const { totalPrice, formattedProductPrice } = useProductPrice({
    shouldIncludeModifications: true,
    shouldIncludeQuantity: true,
  });
  const { dayParts, isItemAvailableForDaypart, itemDayParts } = useProductAvailability();

  const { incentiveNotInMenu, limitPerOrderMet, rewardPointCost } = useRedeemReward();
  const rewardRedemptionBenefitIdParam = useGetUrlSearchParam('reward');
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const stagedCartPoints = useAppSelector(selectors.loyalty.selectStagedCartPoints);
  const { rewardLimitePerOrder, isLimitRewardRedemptionPerOrderReached } =
    useLoyaltyRewards(loyaltyUser);

  const { availableCartQuantity, maxCartQuantityMet: isLimitReached } = getMenuObjectCartQuantity({
    menuObject: selectedProduct,
    quantityToAdd: productQuantity,
    cartEntries,
    cartIdEditing,
  });
  const maxCartQuantityMet = isLimitReached && !editingCartEntry;

  const mParticle = useMParticleContext();
  const handleQuantityUpdate = useCallback(
    value => {
      mParticle.logRBIEvent({
        name: CustomEventNames.CLICK_EVENT,
        type: EventTypes.Navigation,
        attributes: {
          component: ClickEventComponentNames.BUTTON,
          text: 'Product Incrementor',
        },
      });
      const rewardApplied = appliedLoyaltyRewards[cartIdEditing];
      if (value < productQuantity && rewardApplied?.timesApplied > 1) {
        dispatch(
          actions.loyalty.unApplyReward({
            rewardBenefitId: rewardApplied.rewardId,
            cartId: cartIdEditing,
            loyaltyUser,
          })
        );
      }
      setProductQuantity(value);
    },
    [
      appliedLoyaltyRewards,
      cartIdEditing,
      dispatch,
      loyaltyUser,
      mParticle,
      productQuantity,
      setProductQuantity,
    ]
  );

  // If a loyalty incentive (offer, reward) is not available as a regular menu item,
  // the incrementor also increments incentive redemptions in addition to adjusting quantity.
  // This takes into account user point and determines whether there are
  // sufficient points for further redemptions.
  const isIncrementDisabledForRewardRedemption = () => {
    if (limitPerOrderMet) {
      return true;
    }
    if (!incentiveNotInMenu || !loyaltyEnabled) {
      return false;
    }

    if (rewardRedemptionBenefitIdParam) {
      return productQuantity === rewardLimitePerOrder;
    }

    const checkRedemptionEligibility = () => {
      if (rewardRedemptionBenefitIdParam) {
        const incrementedPointCost = rewardPointCost * (productQuantity + 1);
        return stagedCartPoints <= incrementedPointCost;
      }
      return false;
    };
    return !!cartIdEditing || checkRedemptionEligibility();
  };

  const isDisableForRewardRedemption = useCallback(() => {
    if (rewardRedemptionBenefitIdParam) {
      return isLimitRewardRedemptionPerOrderReached;
    }

    return false;
  }, [rewardRedemptionBenefitIdParam, isLimitRewardRedemptionPerOrderReached]);

  const showIncrementor = !isOffer && !maxCartQuantityMet && isItemAvailableForDaypart;
  const disableIncrementQuantity = maxCartQuantityMet || isIncrementDisabledForRewardRedemption();

  const maxRewardToRedeem = rewardLimitePerOrder || availableCartQuantity;

  const disableIncrementor =
    maxCartQuantityMet || (loyaltyEnabled && incentiveNotInMenu && !!cartIdEditing);

  // If price is not set correctly, don't allow adding such an item to cart, except offers
  const isCurrentPriceValid = totalPrice() > 0 || isOffer;
  const productName = selectedProduct?.name?.locale;

  const messageId = editingCartEntry
    ? 'updateQuantityAndItemWithPrice'
    : 'addQuantityAndItemWithPrice';

  const ariaLabel = formatMessage(
    { id: messageId },
    { itemName: productName, quantity: productQuantity, price: formattedProductPrice }
  );

  const addToOrderText = formatMessage({
    id: editingCartEntry ? 'update' : 'addToCart',
  });

  const label = maxCartQuantityMet
    ? formatMessage({ id: 'limitReached' })
    : `${addToOrderText} - ${formattedProductPrice}`;

  const handleClick = () => {
    mParticle.logRBIEvent({
      name: CustomEventNames.CLICK_EVENT,
      type: EventTypes.Navigation,
      attributes: {
        component: ClickEventComponentNames.BUTTON,
        text: editingCartEntry ? 'Product Update' : 'Product Addition',
      },
    });
    addToOrder();
  };

  return (
    <ProductCtaWrapper
      button={
        isItemAvailableForDaypart ? (
          <StyledActionButton
            aria-label={ariaLabel}
            data-testid="product-home-action-button"
            disabled={!isCurrentPriceValid || maxCartQuantityMet || isDisableForRewardRedemption()}
            onClick={handleClick}
            fullWidth
            maintainWidth
          >
            {label}
          </StyledActionButton>
        ) : (
          <ItemUnavailableMessage
            dayParts={dayParts}
            itemDayParts={itemDayParts}
            marginAndBorders={false}
          />
        )
      }
      incrementor={
        showIncrementor && (
          <StyledNumberInputWrapper>
            <NumberInput
              aria-label={`Quantity ${productQuantity} of ${productName}`}
              value={productQuantity}
              onChange={handleQuantityUpdate}
              min={MIN_CART_QUANTITY}
              // specific check for reward limit
              max={!!rewardRedemptionBenefitIdParam ? maxRewardToRedeem : availableCartQuantity}
              disableIncrement={disableIncrementQuantity}
              disabled={disableIncrementor}
              decrement-aria-label={`decrement-${productName}`}
              increment-aria-label={`increment-${productName}`}
            />
          </StyledNumberInputWrapper>
        )
      }
    />
  );
};
